import React, {useState, useEffect} from 'react';
import {CallEvent} from 'entities/event';
import axios from 'axios';
import AudioPlayer from './AudioPlayer';
import CallTranscript from './CallTranscript';
import i18n from 'i18n';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import CellLoader from '@/components/loading/CellLoader';
import {buildEventURL} from '@/utils/urlBuilder';


export interface Props {
    eventData: CallEvent;
    autoScrollScope?: 'event' | 'search' | 'textSamples';
}

const CallDetailBlock = React.memo<Props>(({eventData, autoScrollScope = 'event'}) => {
    const {config, loading: configLoading} = useOptimizedConfig();
    const [transcript, setTranscript] = useState<any>(null);
    const [waveSurfer, setWaveSurfer] = useState(null);
    const [loadingTranscript, setLoadingTranscript] = useState(false);
    const [showLoader, setShowLoader] = useState(false);

    useEffect(() => {
        if (!config) {return;}

        const getData = async () => {
            let response;
            try {
                setLoadingTranscript(true);
                response = await axios.get(buildEventURL('getCallDetailViewData', eventData.event_id), {
                    headers: {'Content-Type': 'application/json'},
                    data: {},
                });
                setTranscript(response.data);
            } catch (e: any) {
                if (e.response.status === 404) {
                    return;
                } else {
                    throw e;
                }
            } finally {
                setLoadingTranscript(false);
            }
        };
        getData();
    }, [eventData.event_id, config]);

    // Avoid showing a "flash" of the loader for very fast responses
    useEffect(() => {
        let timer: number | undefined;
        if (loadingTranscript) {
            timer = window.setTimeout(() => setShowLoader(true), 200);
        } else {
            setShowLoader(false);
        }

        return () => {
            if (timer) {
                window.clearTimeout(timer);
            }
        };
    }, [loadingTranscript]);

    // Show loading while config is loading
    if (configLoading || !config) {
        return <CellLoader text={i18n.t('loading_configuration.text')} />;
    }

    if (loadingTranscript && showLoader) {
        return <CellLoader fullHeight={true} />;
    }

    const handleWaveSurfer = (waveSurferInstance: React.SetStateAction<null>) => {
        setWaveSurfer(waveSurferInstance);
    };

    return (
        <>
            {transcript ? (
                <>
                    <AudioPlayer
                        eventId={eventData.event_id}
                        timelineBlock={transcript.timeline_block}
                        timelineBlockWords={transcript.timeline_block_words}
                        isMono={transcript.is_mono}
                        onWaveSurferReady={handleWaveSurfer}
                        autoScrollScope={autoScrollScope}
                    />

                    <div className="mt-4" />
                    <CallTranscript
                        eventId={eventData.event_id}
                        timelineBlock={transcript.timeline_block}
                        transcriptAvailable={!('error' in transcript)}
                        isMono={transcript.is_mono}
                        agentChannelId={transcript.agent_channel_id}
                        waveSurfer={waveSurfer}
                        autoScrollScope={autoScrollScope}
                    />
                </>
            ) : (
                <span style={{fontSize: '0.9rem'}} className="d-block mt-1 text-muted">{i18n.t('jt_data_available.text')}</span>
            )}
        </>
    );

});

export default CallDetailBlock;
